import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import moment from 'moment-timezone';
import { StarIcon } from 'lucide-react';
import { useConfirm } from '@/components/confirm/ConfirmProvider';
import { DateField } from '@/components/DateField';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useApi, useNavigate } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { formatMoney as formatMoneyJs } from '../../../utils';
import { isEmpty as isEmptyJs } from '../../../ui/utils';
import { hasMixedDataTypes as hasMixedDataTypesJs } from '../../../utils/hasMixedDataType';
import { usePolicyPageContext as usePolicyPageContextJs } from '../context/PolicyPageContext';
import { Grid, type Row } from '../tabParts';
import CombineLocationModal from './CombineLocationModal';

/* eslint-disable @typescript-eslint/no-explicit-any -- boundary: the untyped JavaScript helpers and context below, and AG Grid's cell params */
const formatMoney = formatMoneyJs as (value: any, options?: { prefix?: string; withDecimals?: boolean }) => string;
const isEmpty = isEmptyJs as (value: any) => boolean;
const hasMixedDataTypes = hasMixedDataTypesJs as (values: any[]) => boolean;
type CellParams = any;
type Moment = moment.Moment;
type PolicyPageState = {
  setCurrentTab?: (tab: string) => void;
  setMCEsData: (update: (prev: Row[]) => Row[]) => void;
  fetchMonthlyStatements?: () => Promise<unknown>;
  executedMonthlyStatements?: Row[];
  editMonthlyReporting: boolean;
  setMonthlyReportingData: (data: Row) => void;
  monthlyReportingData: Row;
  MCEsData: Row[];
  combinedMode: boolean;
  groups: Row[];
  showMCEColumns: boolean;
  refreshMCEs: () => void;
};
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;
/* eslint-enable @typescript-eslint/no-explicit-any */

const money = (value: unknown) => formatMoney(value, { prefix: '$', withDecimals: true });

// DateField works in calendar dates; these dialogs hold moments.
const calendarDateOf = (m: Moment) => new Date(m.year(), m.month(), m.date());

const CreateRetroMCEModal = ({
  showModal,
  setShowModal,
  policyId,
  getPayload,
  createMCE,
}: {
  showModal: boolean;
  setShowModal: (open: boolean) => void;
  policyId: string;
  getPayload: () => Row;
  createMCE: (policyId: string, payload: Row) => Promise<Row[] | null | undefined>;
}) => {
  const navigate = useNavigate();
  const { setCurrentTab = () => {}, setMCEsData } = usePolicyPageContext();
  const payload = getPayload();
  const [effectiveAt, setEffectiveAt] = useState(() => moment.utc(payload.effectiveAt));
  const [effectiveUntil, setEffectiveUntil] = useState(() => moment.utc(payload.effectiveUntil));
  const [inProgress, setInProgress] = useState(false);

  const handleOnSubmit = async () => {
    setInProgress(true);

    try {
      const updatedPayload = {
        ...payload,
        effectiveAt: moment(effectiveAt).format('YYYY-MM-DD'),
        effectiveUntil: moment(effectiveUntil).format('YYYY-MM-DD'),
      };

      const response = await createMCE(policyId, updatedPayload);
      if (!response || response.length === 0) {
        notifyError('Could not create retrospective MCE. Please ask admin.');
      } else {
        setMCEsData((prevData) => [...prevData, ...response]);

        if (response.length === 1) {
          const mceId = response[0]?.id;
          navigate.to(`/policies/${policyId}/mces/${mceId}`);
        } else if (response.length > 1) {
          notifySuccess('Created two retrospective MCEs surrounding location change MCE.');
          setCurrentTab('MCES');
        } else {
          console.error('Unexpected response structure: ', response);
        }
      }
    } catch (error) {
      console.error('Failed to create retrospective MCE:', error);
      notifyError('Unable to create the MCE. Please try again later.');
    } finally {
      setShowModal(false);
      setInProgress(false);
    }
  };

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) setShowModal(false);
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="capitalize">Create Retrospective MCE</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          {/* DateField edge: a picked calendar date becomes moment(date) for the payload's YYYY-MM-DD. */}
          <DateField label="Effective At" value={calendarDateOf(effectiveAt)} onChange={(date) => date && setEffectiveAt(moment(date))} />
          <DateField label="Effective Until" value={calendarDateOf(effectiveUntil)} onChange={(date) => date && setEffectiveUntil(moment(date))} />
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button onClick={handleOnSubmit} disabled={inProgress}>
            {inProgress ? <Spinner /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function StatementField({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <div className="mb-1.5 text-xs font-semibold text-text-subdued">{label}</div>
      {children}
    </div>
  );
}

const statusColour = (status: string | undefined) => {
  const s = status?.toLowerCase();
  if (s === 'executed') return 'text-statement-executed';
  if (s === 'generating') return 'text-statement-generating';
  return 'text-statement-draft';
};

export const MonthlyStatementsModal = ({
  open,
  onClose,
  statements = [],
  onExecuteStatement,
  refreshStatements,
  deleteMonthlyStatement,
  getStatementJsonBlob,
  policyId,
}: {
  open: boolean;
  onClose: () => void;
  statements?: Row[];
  onExecuteStatement: (statementId: string, startDate: string, endDate: string) => Promise<unknown>;
  refreshStatements: () => Promise<Row[]>;
  deleteMonthlyStatement: (policyId: string, statementId: string) => Promise<unknown>;
  getStatementJsonBlob: (policyId: string, statementId: string, type: string) => Promise<unknown>;
  policyId: string;
}) => {
  const [executing, setExecuting] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [localStatements, setLocalStatements] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();
  // Drives the (exists) markers + locked blue cells on the reporting grid; refresh it after a
  // delete so the soft-deleted statement's quarter unlocks without a page reload.
  const { fetchMonthlyStatements } = usePolicyPageContext();

  useEffect(() => {
    const fetchData = async () => {
      if (open && refreshStatements) {
        setLoading(true);
        try {
          const updated = await refreshStatements();

          setLocalStatements(updated || []);
        } catch (error) {
          setLocalStatements([]);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Poll while modal is open and any statement is in "generating" status
  const hasGenerating = localStatements.some((s) => s.status?.toLowerCase() === 'generating');
  useEffect(() => {
    if (!open || !hasGenerating || !refreshStatements) return;

    const interval = setInterval(async () => {
      try {
        const updated = await refreshStatements();
        setLocalStatements(updated || []);
      } catch (err) {
        console.error('Error polling statements:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [open, hasGenerating, refreshStatements]);

  useEffect(() => {
    if (statements && !loading) {
      setLocalStatements(statements);
    }
  }, [statements, loading]);

  const handleExecute = async (statementId: string, startDate: string, endDate: string) => {
    setExecuting(statementId);
    try {
      await onExecuteStatement(statementId, startDate, endDate);
      const updated = await refreshStatements();
      setLocalStatements(updated || []);
      notifySuccess('Statement executed successfully!');
    } catch (error) {
      console.error('Failed to execute statement:', error);
      notifyError('Failed to execute statement. Please try again.');
    } finally {
      setExecuting(null);
    }
  };

  const handleDelete = async (stmt: Row) => {
    if (!(await confirm({ title: 'Are you sure you want to delete this statement?', destructive: true }))) return;
    setDeleting(stmt.id);
    try {
      await deleteMonthlyStatement(policyId, stmt.id);
      const updated = await refreshStatements();
      setLocalStatements(updated || []);
      // Soft-deleting the statement (and its quarterly MCE) frees that quarter — refresh the
      // executed-statements context so the (exists) marker + locked blue cells clear immediately.
      if (fetchMonthlyStatements) await fetchMonthlyStatements();
    } catch (error) {
      console.error('Failed to delete statement:', error);
      notifyError('Failed to delete statement.');
    } finally {
      setDeleting(null);
    }
  };

  const handleJsonView = async (stmt: Row, type: string) => {
    try {
      const data = await getStatementJsonBlob(policyId, stmt.id, type);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(`Failed to load ${type} JSON:`, error);
      notifyError(`Failed to load ${type} JSON.`);
    }
  };

  // Display order: Q1 → Q4 (policy-relative quarter), then chronological, then by statement number.
  // The source data is unsorted (the DAO query has no ORDER BY), so we sort here for a stable list.
  const sortedStatements = useMemo(() => {
    const rank = (q: string) => (q === 'Q1' ? 1 : q === 'Q2' ? 2 : q === 'Q3' ? 3 : q === 'Q4' ? 4 : 99);
    return [...localStatements].sort((a, b) => {
      const r = rank(a.quarter) - rank(b.quarter);
      if (r) return r;
      const da = a.startDate ? new Date(a.startDate).getTime() : 0;
      const db = b.startDate ? new Date(b.startDate).getTime() : 0;
      if (da !== db) return da - db;
      return String(a.monthlyStatementNumber || '').localeCompare(String(b.monthlyStatementNumber || ''));
    });
  }, [localStatements]);

  const value = 'text-sm text-page-meta-strong';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-h-[80vh] w-[90%] max-w-[1300px] gap-0 overflow-hidden p-0 sm:max-w-[1300px]">
        <DialogHeader className="border-b border-statement-border p-6">
          <DialogTitle className="text-xl font-semibold text-page-meta-strong">Quarterly Statements</DialogTitle>
        </DialogHeader>
        <div className="max-h-[calc(80vh-80px)] overflow-y-auto p-6">
          {loading ? (
            <p className="p-6 text-center text-sm text-text-subdued">Loading statements...</p>
          ) : localStatements.length === 0 ? (
            <p className="p-6 text-center text-sm text-text-subdued">No quarterly statements found for this policy.</p>
          ) : (
            <div className="flex flex-col gap-3">
              {sortedStatements.map((stmt) => {
                const status = stmt.status?.toLowerCase();
                const missing = (stmt.monthsMissingDeclaredValues || []).join(', ') || 'some months';
                return (
                  <div
                    key={stmt.id}
                    className={cn('rounded-lg border border-statement-border p-4', status === 'executed' ? 'bg-statement-executed-bg' : 'bg-white')}
                  >
                    <div className="flex flex-row items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-row flex-wrap items-start gap-3">
                          <StatementField label="Quarterly Statement" className="min-w-[260px]">
                            <div className="text-[13px] font-medium text-page-meta-strong">{stmt.monthlyStatementNumber || '-'}</div>
                          </StatementField>
                          <StatementField label="Quarter" className="min-w-[55px]">
                            <div className={value}>{stmt.quarter || '-'}</div>
                          </StatementField>
                          <StatementField label="Start Date" className="min-w-[95px]">
                            <div className={value}>{moment(stmt.startDate).format('MMM DD, YYYY')}</div>
                          </StatementField>
                          <StatementField label="End Date" className="min-w-[95px]">
                            <div className={value}>{moment(stmt.endDate).format('MMM DD, YYYY')}</div>
                          </StatementField>
                          <StatementField label="Status" className="min-w-[70px]">
                            <div className="flex flex-row items-center gap-1">
                              <span className={cn('text-sm font-medium', statusColour(stmt.status))}>{stmt.status}</span>
                              {stmt.isComplete === false && (
                                <span className="cursor-help text-sm text-statement-danger" title={`Missing Declared TIVs: ${missing}`}>
                                  ⚠
                                </span>
                              )}
                            </div>
                          </StatementField>
                          <StatementField label="Generated At" className="min-w-[95px]">
                            <div className={value}>{stmt.generatedAt ? moment(stmt.generatedAt).format('MMM DD, YYYY') : '-'}</div>
                          </StatementField>
                          <StatementField label="Generated By" className="min-w-[95px]">
                            <div className={value}>{stmt.generatedBy || '-'}</div>
                          </StatementField>
                          <StatementField label="Premium" className="min-w-[80px]">
                            <div className={value}>{Number.isFinite(stmt.premium) ? money(stmt.premium) : '-'}</div>
                          </StatementField>
                          <StatementField label="Fees" className="min-w-[65px]">
                            <div className={value}>{Number.isFinite(stmt.fees) ? money(stmt.fees) : '-'}</div>
                          </StatementField>
                          <StatementField label="Total" className="min-w-[80px]">
                            <div className={value}>{Number.isFinite(stmt.totalDue) ? money(stmt.totalDue) : '-'}</div>
                          </StatementField>
                        </div>

                        {/* Excel + JSON always on their own row below — space is tight once the Execute
                            button is present (draft rows), so keep the layout consistent for all cards. */}
                        <div className="mt-2.5 flex flex-row flex-wrap items-start gap-3">
                          <StatementField label="Excel" className="min-w-[70px]">
                            {stmt.downloadUrl ? (
                              <a
                                href={stmt.downloadUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-sm font-medium text-statement-accent underline"
                              >
                                Download
                              </a>
                            ) : (
                              <span className="text-sm text-statement-empty">—</span>
                            )}
                          </StatementField>
                          <StatementField label="JSON" className="min-w-[120px]">
                            <div className="flex flex-row gap-2">
                              {['monthly', 'per-location', 'quarterly'].map((type) => (
                                <a
                                  key={type}
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleJsonView(stmt, type);
                                  }}
                                  className="cursor-pointer text-xs text-statement-accent underline"
                                >
                                  {type}
                                </a>
                              ))}
                            </div>
                          </StatementField>
                        </div>
                        {(stmt.engineVersion || stmt.buildRef) && (
                          <p className="mt-2 text-xs text-text-subdued">{`engine ${stmt.engineVersion || '—'} · build ${stmt.buildRef || '—'}`}</p>
                        )}
                      </div>

                      <div className="ml-6 flex flex-row items-center gap-2">
                        {status === 'draft' && (
                          <span title={stmt.isComplete === false ? `Cannot execute: missing Declared TIVs for ${missing}` : ''}>
                            <Button
                              onClick={() => handleExecute(stmt.id, stmt.startDate, stmt.endDate)}
                              disabled={executing === stmt.id || stmt.isComplete === false}
                              className="disabled:opacity-60"
                            >
                              {executing === stmt.id ? 'Executing...' : 'Execute'}
                            </Button>
                          </span>
                        )}
                        {status !== 'executed' && (
                          <Button
                            variant="ghost"
                            onClick={() => handleDelete(stmt)}
                            disabled={deleting === stmt.id}
                            className="text-[13px] text-statement-danger hover:text-statement-danger disabled:opacity-60"
                          >
                            {deleting === stmt.id ? 'Deleting...' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function BillingSelect({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled: boolean;
}) {
  const id = useId();
  return (
    <div className="flex min-w-[180px] flex-1 flex-col gap-1.5">
      <Label htmlFor={id} className="text-[13px] font-medium text-text-subdued">
        {label}
      </Label>
      <Select items={options} value={value || null} onValueChange={(next) => onChange(String(next ?? ''))} disabled={disabled}>
        <SelectTrigger id={id} className={cn('w-full bg-white font-semibold', value ? 'text-page-meta-strong' : 'text-select-placeholder')}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const sectionHeading = 'mb-2 text-[13px] font-semibold tracking-[0.5px] text-text-subdued uppercase';

const CreateMonthlyStatementModal = ({
  showModal,
  setShowModal,
  policyId,
  insertMonthlyStatement,
  refreshStatements,
  getQuarterlyDates,
  getBillingConfig,
  putBillingConfig,
  getLatestStatementCommissionRate,
}: {
  showModal: boolean;
  setShowModal: (open: boolean) => void;
  policyId: string;
  insertMonthlyStatement: (payload: Row) => Promise<Row | undefined>;
  refreshStatements?: () => Promise<unknown>;
  getQuarterlyDates?: (policyId: string) => Promise<Row>;
  getBillingConfig?: (policyId: string) => Promise<Row>;
  putBillingConfig: (policyId: string, payload: Row) => Promise<unknown>;
  getLatestStatementCommissionRate?: (policyId: string) => Promise<Row>;
}) => {
  const [quarters, setQuarters] = useState<Row[]>([]);
  const [loadingDates, setLoadingDates] = useState(true);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [collateralType, setCollateralType] = useState('');
  const [billingType, setBillingType] = useState('');
  const [loadingBillingConfig, setLoadingBillingConfig] = useState(true);
  // Statement commission percent ('' = bill the policy's own brokerage). Stored on the statement it produces;
  // pre-filled from the policy's most recent statement so the quarters of a term bill the same rate.
  const [statementCommissionRate, setStatementCommissionRate] = useState('');
  // Debug: adds the LOCATION PREMIUM ANALYSIS section (each location's monthly premium) to the statement Excel.
  const [includePerLocPremiumAnalysis, setIncludePerLocPremiumAnalysis] = useState(false);
  const { executedMonthlyStatements } = usePolicyPageContext();
  const confirm = useConfirm();
  const commissionId = useId();
  const debugId = useId();

  useEffect(() => {
    if (!policyId || !getQuarterlyDates) return;
    setLoadingDates(true);
    getQuarterlyDates(policyId)
      .then((response) => {
        setQuarters(response?.data?.quarters || []);
      })
      .catch((err) => {
        console.error('Failed to load quarterly dates:', err);
      })
      .finally(() => setLoadingDates(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  useEffect(() => {
    if (!policyId || !getBillingConfig) return;
    setLoadingBillingConfig(true);
    getBillingConfig(policyId)
      .then((response) => {
        const data = response?.data || response;
        setCollateralType(data?.collateral_type || '');
        setBillingType(data?.billing_type || '');
      })
      .catch((err) => {
        console.error('Failed to load billing config:', err);
      })
      .finally(() => setLoadingBillingConfig(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  useEffect(() => {
    if (!policyId || !getLatestStatementCommissionRate) return;
    getLatestStatementCommissionRate(policyId)
      .then((response) => {
        const data = response?.data || response;
        if (data?.statement_commission_rate != null) setStatementCommissionRate(String(data.statement_commission_rate));
      })
      .catch((err) => {
        console.error('Failed to load the last statement commission rate:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const selectedData = quarters.find((q) => q.quarter === selectedQuarter);

  const hasOverlap = useMemo(() => {
    if (!selectedQuarter) return false;
    return (executedMonthlyStatements || []).some((stmt) => stmt.quarter === selectedQuarter);
  }, [selectedQuarter, executedMonthlyStatements]);

  const billingConfigReady = collateralType && billingType;

  const commissionRateText = String(statementCommissionRate).trim();
  const commissionRateError = (() => {
    if (commissionRateText === '') return null;
    const n = Number(commissionRateText);
    if (!Number.isFinite(n)) return 'Enter a number, such as 7.5';
    if (n < 0 || n >= 100) return 'Enter a percent from 0 to just under 100';
    return null;
  })();

  const doCreate = async () => {
    setInProgress(true);

    try {
      await putBillingConfig(policyId, {
        collateral_type: collateralType,
        billing_type: billingType,
      });

      const response = await insertMonthlyStatement({
        status: 'draft',
        policyId,
        quarter: selectedQuarter,
        ...(commissionRateText !== '' ? { statementCommissionRate: Number(commissionRateText) } : {}),
        ...(includePerLocPremiumAnalysis ? { includePerLocPremiumAnalysis: true } : {}),
      });

      if (response?.status === 'success') {
        if (refreshStatements) {
          await refreshStatements();
        }

        // If the backend returned a downloadUrl (sync path), download immediately
        if (response.downloadUrl) {
          const link = document.createElement('a');
          link.href = response.downloadUrl;
          link.download = `${response.monthlyStatementNumber || 'monthly-statement'}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        console.error('Failed to create monthly statement.');
        notifyError('Failed to create monthly statement.');
      }
    } catch (error) {
      console.error('Failed to create monthly statement:', error);
      // A 400 carries the reason and how to fix it (e.g. stale monthly reporting totals, an invalid commission rate).
      const failure = error as { data?: { message?: string }; response?: { data?: { message?: string } } } | undefined;
      const serverMessage = failure?.data?.message || failure?.response?.data?.message;
      notifyError(serverMessage || 'Unable to create the monthly statement. Please try again later.');
    } finally {
      setInProgress(false);
      setShowModal(false);
    }
  };

  const handleOnSubmit = async () => {
    if (selectedData && selectedData.isComplete === false) {
      const proceed = await confirm({
        title: '⚠ Missing Declared TIVs',
        description: `${selectedQuarter} has missing Declared TIVs for some locations. The statement will be created with incomplete data. Do you want to continue?`,
        confirmLabel: 'Continue Anyway',
      });
      if (!proceed) return;
    }
    doCreate();
  };

  const isLoading = inProgress || loadingDates || loadingBillingConfig;

  return (
    <Dialog
      open={showModal}
      onOpenChange={(open) => {
        if (!open) setShowModal(false);
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="capitalize">Create Monthly Statement</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto py-2">
          <div className="mb-4">
            <div className={sectionHeading}>Billing Configuration</div>
            <div className="flex flex-row flex-wrap gap-3">
              <BillingSelect
                label="Collateral Type"
                value={collateralType}
                onChange={setCollateralType}
                disabled={inProgress}
                options={[
                  { value: 'cash', label: 'Cash' },
                  { value: 'letter_of_credit', label: 'Letter of Credit' },
                ]}
              />
              <BillingSelect
                label="Billing Type"
                value={billingType}
                onChange={setBillingType}
                disabled={inProgress}
                options={[
                  { value: 'direct', label: 'Direct' },
                  { value: 'agency_bill', label: 'Agency Bill' },
                ]}
              />
            </div>
            <div className="mt-3 flex flex-col gap-1.5">
              <Label htmlFor={commissionId} className="text-[13px] font-medium text-text-subdued">
                Statement commission %
              </Label>
              <Input
                id={commissionId}
                type="text"
                inputMode="decimal"
                placeholder="Policy brokerage"
                value={statementCommissionRate}
                onChange={(e) => setStatementCommissionRate(e.target.value)}
                disabled={inProgress}
                aria-invalid={commissionRateError ? true : undefined}
                className="bg-white font-semibold text-page-meta-strong"
              />
              <p className={cn('text-xs', commissionRateError ? 'text-statement-danger' : 'text-text-subdued')}>
                {commissionRateError ||
                  "Leave blank to bill the policy's brokerage commission. A rate here is saved on this statement and pre-filled for the next one."}
              </p>
            </div>
          </div>

          <div className={sectionHeading}>Quarter</div>
          <div className="flex flex-col gap-2">
            {loadingDates ? (
              <p className="text-sm text-text-subdued">Loading quarter dates...</p>
            ) : (
              quarters.map((q) => {
                const isSelected = selectedQuarter === q.quarter;
                const isOverlap = (executedMonthlyStatements || []).some((stmt) => stmt.quarter === q.quarter);

                return (
                  <button
                    key={q.quarter}
                    type="button"
                    aria-label={`${q.quarter} · ${q.effective_at} — ${q.effective_until}${isOverlap ? ' (exists)' : ''}`}
                    aria-pressed={isSelected}
                    onClick={() => !inProgress && !isOverlap && setSelectedQuarter(q.quarter)}
                    className={cn(
                      'rounded-[10px] px-5 py-3.5 text-center text-[15px] font-bold tracking-[0.5px] transition-all duration-150',
                      isOverlap
                        ? 'cursor-not-allowed border border-statement-border bg-quarter-exists-bg text-quarter-exists-fg opacity-40'
                        : isSelected
                        ? 'border-2 border-statement-accent bg-statement-accent text-white shadow-[0_2px_8px_rgba(123,31,162,0.3)]'
                        : 'border border-panel-border bg-white text-page-meta-strong shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
                      !isOverlap && (inProgress ? 'cursor-wait' : 'cursor-pointer')
                    )}
                  >
                    {q.quarter} &nbsp;&middot;&nbsp; {q.effective_at} — {q.effective_until}
                    {isOverlap ? '  (exists)' : ''}
                    {!isOverlap && q.isComplete === false && (
                      <span
                        className={cn('ml-2', isSelected ? 'text-quarter-warning-on' : 'text-statement-danger')}
                        title="Missing Declared TIVs for some locations"
                      >
                        ⚠
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="mt-4 flex flex-row items-start gap-2 text-[13px] text-text-subdued">
            <Checkbox
              id={debugId}
              className="mt-0.5"
              checked={includePerLocPremiumAnalysis}
              onCheckedChange={(checked) => setIncludePerLocPremiumAnalysis(checked === true)}
              disabled={inProgress}
            />
            <Label htmlFor={debugId} className="block text-[13px] font-normal text-text-subdued">
              <span className="font-semibold text-page-meta-strong">Debug: add per-location premium breakdown</span>
              <br />
              Adds a Location Premium Analysis section to the Excel showing each location&apos;s monthly premium.
            </Label>
          </div>
        </div>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button onClick={handleOnSubmit} disabled={isLoading || !selectedQuarter || hasOverlap || !billingConfigReady || !!commissionRateError}>
            {isLoading ? <Spinner /> : null}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** A full-width action inside the reporting grid's button row. */
function GridAction({ label, onClick, disabled = false }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <div className="my-2 rounded-lg border border-grid-action-outline">
      <Button
        variant="ghost"
        className="min-h-[50px] w-full px-2 py-1 text-primary hover:text-primary disabled:opacity-60"
        disabled={disabled}
        onClick={onClick}
      >
        {label}
      </Button>
    </div>
  );
}

type MonthlyReportingProps = {
  policy?: Row;
  setGridReference: (ref: unknown) => void;
  showStatementsListModal: boolean;
  setShowStatementsListModal: (open: boolean) => void;
};

export const MonthlyReporting = forwardRef<unknown, MonthlyReportingProps>((props, _ref) => {
  const { policy = { locations: [] }, setGridReference, showStatementsListModal, setShowStatementsListModal } = props;
  const {
    createMCE,
    insertMonthlyStatement,
    deleteMonthlyStatement,
    getStatementJsonBlob,
    getMonthlyStatements,
    executeMonthlyStatement,
    getQuarterlyDates,
    getBillingConfig,
    putBillingConfig,
    getLatestStatementCommissionRate,
  } = useApi();
  const {
    editMonthlyReporting,
    setMonthlyReportingData,
    monthlyReportingData,
    MCEsData,
    combinedMode,
    groups,
    showMCEColumns,
    executedMonthlyStatements,
    refreshMCEs,
  } = usePolicyPageContext();
  const [rowData, setRowData] = useState<Row[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showCombineLocationsModal, setShowCombineLocationsModal] = useState(false);
  const [sortByGroup, setSortByGroup] = useState(false);
  let updateTimeout: ReturnType<typeof setTimeout> | null = null;
  const [showMonthlyStatementModal, setShowMonthlyStatementModal] = useState(false);
  const [allStatements, setAllStatements] = useState<Row[]>([]);
  const [executedStatements, setExecutedStatements] = useState<Row[]>([]);
  const gridRef = useRef<unknown>(null);
  const sortName = useId();

  const getMceEffectiveAtMoment = (mce: Row) => {
    const raw = mce?.effectiveAt || mce?.effective_at || null;
    return moment.utc(raw, [moment.ISO_8601, 'YYYY-MM-DD', 'MM/DD/YYYY'], true);
  };

  const getMceEffectiveAtTs = (mce: Row) => {
    const m = getMceEffectiveAtMoment(mce);
    return m.isValid() ? m.valueOf() : Number.NaN;
  };

  const executedMCEs = useMemo(() => {
    return (MCEsData || [])
      .filter(
        (mce) =>
          mce.type === 'locationChange' &&
          (mce.status === 'executed' || mce.status === 'released') &&
          mce.status !== 'reversal' &&
          mce.status !== 'superseded'
      )
      .sort((a, b) => {
        const ta = getMceEffectiveAtTs(a);
        const tb = getMceEffectiveAtTs(b);
        const aValid = Number.isFinite(ta);
        const bValid = Number.isFinite(tb);
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1; // invalid dates last
        if (!bValid) return -1;
        return ta - tb;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MCEsData]);

  const fetchAllStatements = useCallback(async (): Promise<Row[]> => {
    if (!policy?.id) return [];

    try {
      const response: Row[] | undefined = await getMonthlyStatements(policy.id);

      setAllStatements(response || []);
      const executedOnly =
        response
          ?.filter((stmt) => stmt.status?.toLowerCase() === 'executed')
          .map((stmt) => ({
            effectiveAt: stmt.startDate,
            effectiveUntil: stmt.endDate,
            status: stmt.status,
            quarter: stmt.quarter,
          })) || [];

      setExecutedStatements(executedOnly);
      return response || [];
    } catch (error) {
      console.error('Failed to fetch monthly statements:', error);
      return [];
    }
  }, [policy?.id, getMonthlyStatements]);

  const handleExecuteStatement = async (statementId: string, startDate: string, endDate: string) => {
    try {
      const response = await executeMonthlyStatement(statementId);
      if (response?.status === 'success') {
        console.log('Statement executed successfully');

        setExecutedStatements((prev) => [
          ...prev,
          {
            effectiveAt: startDate,
            effectiveUntil: endDate,
            status: 'executed',
          },
        ]);

        // Refresh MCEs to pick up the newly created quarterly MCE
        refreshMCEs();

        setTimeout(() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: the legacy Grid's reference holder
          const holder = setGridReference as any;
          if (holder?.current) {
            holder.current.api.refreshCells({
              force: true,
              suppressFlash: false,
            });
          }
        }, 0);

        return true;
      }
      throw new Error('Failed to execute statement');
    } catch (error) {
      console.error('Error executing statement:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchAllStatements();
  }, [fetchAllStatements]);

  const isMonthLocked = useCallback(
    (monthDate: string | undefined) => {
      if (!monthDate) return false;

      return (executedMonthlyStatements || []).some((stmt) => {
        const start = stmt.startDate || stmt.effectiveAt;
        const end = stmt.endDate || stmt.effectiveUntil;
        if (!start || !end) return false;

        const stmtStart = moment(start).startOf('month');
        const stmtEnd = moment(end).endOf('month');
        const cellMonth = moment(monthDate).startOf('month');

        return cellMonth.isSameOrAfter(stmtStart, 'month') && cellMonth.isSameOrBefore(stmtEnd, 'month');
      });
    },
    [executedMonthlyStatements]
  );

  const handleCheckboxChange = (checked: boolean, columnIndex: number) => {
    if (checked) {
      setSelectedColumns((prevColumns) => [...prevColumns, columnIndex]);
    } else {
      setSelectedColumns((prevColumns) => prevColumns.filter((index) => index !== columnIndex));
    }
  };
  function findEmptyMonths(data: Row[], months: string[]) {
    return months.filter((_, monthIndex) => data.every((location) => location.values[monthIndex] === null));
  }

  const formatPayload = useCallback(
    (columnIndex: number) => {
      const payload = {
        months: [monthlyReportingData?.months[columnIndex]],
      };

      return payload;
    },
    [monthlyReportingData]
  );

  const sumValues = (values: unknown[]) => {
    return values.reduce<number>((sum, value) => {
      const numericValue = typeof value === 'string' && !isNaN(parseFloat(value)) ? parseFloat(value) : value;
      if (typeof numericValue === 'number') {
        return sum + numericValue;
      }
      return sum;
    }, 0);
  };
  const emptyMonths = findEmptyMonths(monthlyReportingData?.locations || [], monthlyReportingData?.months || []);

  const getCellStyle = (params: CellParams, date: string) => {
    if (emptyMonths.includes(date)) return null;

    if (params.data.type === 'monthlyInventory') {
      const monthlyValues: unknown[] = [];
      const extractDateFromKey = (key: string) => key.split('_')[1] ?? '';
      Object.entries(params.data as Row).forEach(([key, value]) => {
        const dateMatch = key.includes(`monthlyInventory`);
        if (dateMatch && !emptyMonths.includes(extractDateFromKey(key))) {
          monthlyValues.push(value);
        }
      });

      const hasMixedTypesValues = hasMixedDataTypes(monthlyValues);
      if (hasMixedTypesValues) {
        return { background: 'var(--cell-mixed)' };
      }
    }

    return null;
  };

  const getPayload = useCallback(() => {
    const payloads = selectedColumns.map(formatPayload);

    const combinedPayload = payloads.reduce<Row>((acc, payload) => {
      Object.entries(payload).forEach(([key, value]) => {
        acc[key] = (acc[key] || []).concat(value);
      });
      return acc;
    }, {});

    const sortedMonths: string[] | undefined = combinedPayload?.months?.sort((a: string, b: string) => Number(new Date(a)) - Number(new Date(b)));
    if (!sortedMonths || sortedMonths.length === 0) {
      return {};
    }
    const getFirstOfMonth = (dateString: string) => dateString.slice(0, 7) + '-01';
    const finalPayload = {
      type: 'retrospective',
      effectiveAt: getFirstOfMonth(sortedMonths[0] as string),
      effectiveUntil: sortedMonths[sortedMonths.length - 1],
    };

    return finalPayload;
  }, [selectedColumns, formatPayload]);

  const getRandomColor = (() => {
    const colors: Record<string, string> = {};
    const initialColors = ['hsl(220, 97.10%, 58.80%)', 'hsl(0, 70%, 50%)', 'hsl(140, 70%, 50%)'];
    let colorsAdded = 0;
    let hueValue = 0;
    const step = 137;
    return (id: string) => {
      if (!colors[id]) {
        if (colorsAdded < initialColors.length) {
          colors[id] = initialColors[colorsAdded] as string;
          colorsAdded++;
        } else {
          while ((hueValue > 200 && hueValue < 250) || (hueValue > 90 && hueValue < 155) || hueValue === 0) {
            hueValue += step;
          }
          colors[id] = `hsl(${hueValue % 360}, 70%, 50%)`;
          hueValue += step;
        }
      }
      return colors[id];
    };
  })();

  const locationToParentMap = new Map();
  groups.forEach((group) => {
    group.children.forEach((child: Row) => {
      locationToParentMap.set(child.locationId, group.parentId);
    });
  });

  const parentToChildren: Record<string, string[]> = {};
  groups.forEach((group) => {
    parentToChildren[group.parentId] = group.children.map((child: Row) => child.locationId);
  });

  const parentIdColorMap = new Map();
  groups.forEach((group) => {
    parentIdColorMap.set(group.parentId, getRandomColor(group.parentId));
  });

  const createColumns = () => {
    const baseColumns = [
      {
        headerName: 'Declared Locations',
        children: [
          {
            headerName: 'RiskId',
            field: 'riskId',
            minWidth: 100,
            cellRenderer: (params: CellParams) => {
              const parentId = locationToParentMap.get(params.data?.id);
              const color = parentId ? parentIdColorMap.get(parentId) : parentIdColorMap.get(params.data?.id);
              if (color && combinedMode) {
                return (
                  <div className="flex items-center">
                    {params.value}
                    {parentId ? (
                      <span className="ml-2.5 size-2.5 rounded-full" style={{ backgroundColor: color }}></span>
                    ) : (
                      <StarIcon className="ml-2 size-[15px]" fill={color} stroke="none" aria-hidden />
                    )}
                  </div>
                );
              } else return params.value !== undefined ? params.value : '';
            },
          },
          {
            headerName: 'Name',
            field: 'name',
            minWidth: 200,
            cellRenderer: (params: CellParams) => {
              if (params.data.name === 'Combine Locations') {
                return <GridAction label="Combine Locations" disabled={!combinedMode} onClick={() => setShowCombineLocationsModal(true)} />;
              } else return params.value !== undefined ? params.value : ''; // Avoid blank cells
            },
            cellStyle: (params: CellParams) => {
              if (params.data.name === 'Combine Locations') {
                return { textAlign: 'center' };
              }
              return null;
            },
          },
          {
            headerName: 'Address',
            field: 'address',
            minWidth: 250,
            cellClassRules: {
              'blue-background': (params: CellParams) => {
                return [
                  '% of Bound',
                  'Total',
                  'Date Reported',
                  'Preparer',
                  'File Type',
                  'Understory Checked',
                  'Understory Signed Off',
                  'Include in MCE',
                ].includes(params.data.address);
              },
            },
            cellRenderer: (params: CellParams) => {
              if (params.data.address === 'Create MCE') {
                return (
                  <GridAction
                    label="Create Retroactive MCE"
                    onClick={() => {
                      if (!selectedColumns || selectedColumns.length === 0) {
                        notifyError('Please select month.');
                      } else {
                        setShowModal(true);
                      }
                    }}
                  />
                );
              }
              return params.value !== undefined ? params.value : ''; // Avoid blank cells
            },
            cellStyle: (params: CellParams) => {
              if (params.data.address === 'Create MCE') {
                return { textAlign: 'center' };
              }
              return null;
            },
          },
          {
            headerName: 'Bound TIV',
            field: 'boundTiv',
            minWidth: 250,
            cellRenderer: (params: CellParams) => {
              if (params.data.boundTiv === 'Create Monthly Statement') {
                return <GridAction label="Create Monthly Statement" onClick={() => setShowMonthlyStatementModal(true)} />;
              }

              if (!params.data.formatter || typeof params.value === 'string') {
                return params.value || '';
              }

              if (params.data.address === '% of Bound') {
                return params.value ? `${params.value.toFixed(2)}%` : '';
              }

              const shouldFormatMoney =
                !isEmpty(params.data) && params.data.address !== 'Create MCE' && params.data.name !== 'Combine Locations' && params.value !== null;

              if (shouldFormatMoney) {
                return `${money(params.value)}`;
              }

              return params.value || '';
            },

            cellStyle: (params: CellParams) => {
              if (params.data.boundTiv === 'Create Monthly Statement') {
                return { textAlign: 'center' };
              }
              if (params.data.type === 'label' || (typeof params.data.boundTiv === 'string' && params.data.boundTiv !== 'Create Monthly Statement')) {
                return {
                  border: '1px solid black',
                  fontWeight: 'bold',
                };
              } else if (params.data.address === 'Total') {
                return {
                  fontWeight: 'bold',
                };
              }
              return null;
            },
          },
        ],
      },
    ];

    const sortColumnsByDate = (mceColumns: Row[], monthlyColumns: Row[]) => {
      return [...mceColumns, ...monthlyColumns].sort((a, b) => {
        const getDate = (col: Row) => {
          if (col.headerName.includes('MCE')) {
            return new Date(col.headerName.split(' ')[0]);
          }
          return new Date(col.headerName);
        };

        return Number(getDate(a)) - Number(getDate(b));
      });
    };

    const monthlyColumns: Row[] =
      monthlyReportingData && monthlyReportingData.months
        ? monthlyReportingData.months.map((date: string, index: number) => {
            return {
              headerName: date,
              children: [
                {
                  headerName: 'Declared Inventory Value',
                  field: `monthlyInventory_${date}_${index}`,
                  minWidth: 150,
                  valueFormatter: (params: CellParams) => {
                    if (!params.data.formatter || typeof params.value === 'string') return;
                    if (params.data.address === '% of Bound') {
                      return params.value ? `${params.value.toFixed(2)}%` : '';
                    }
                    return !isEmpty(params.data) &&
                      params.data.address !== 'Create MCE' &&
                      params.data.name !== 'Combine Locations' &&
                      params.data.boundTiv !== 'Create Monthly Statment' &&
                      params.value !== null
                      ? `${money(params.value)}`
                      : '';
                  },

                  cellRenderer: (params: CellParams) => {
                    if (params.data.address === 'Include in MCE') {
                      const field: string = params.colDef.field || '';
                      // Only render checkboxes for the real monthly columns (not MCE columns).
                      if (!field.startsWith('monthlyInventory_')) return null;
                      const match = field.match(/_(\d+)$/);
                      if (!match) return null;
                      const columnIndex = Number(match[1]);
                      const monthDate = monthlyReportingData?.months[columnIndex];
                      const enabled = monthlyReportingData.uwSignedOff[Number(columnIndex)] && editMonthlyReporting;

                      return (
                        <Checkbox
                          aria-label={`Include ${monthDate} in MCE`}
                          className={cn('size-5', enabled ? 'cursor-pointer' : 'cursor-not-allowed')}
                          onCheckedChange={(checked) => handleCheckboxChange(checked === true, columnIndex)}
                          disabled={!monthlyReportingData.uwSignedOff[Number(columnIndex)] || !editMonthlyReporting}
                          checked={selectedColumns.includes(Number(columnIndex))}
                        />
                      );
                    } else if (typeof params.value === 'string') return params.value;
                    else if (params.data.address === '% of Bound') {
                      return params.value ? `${params.value.toFixed(2)}%` : '';
                    }
                    return !isEmpty(params.data) &&
                      params.data.address !== 'Create MCE' &&
                      params.data.name !== 'Combine Locations' &&
                      params.data.boundTiv !== 'Create Monthly Statement' &&
                      params.value !== null
                      ? `${money(params.value)}`
                      : '';
                  },
                  cellEditorSelector: (params: CellParams) => {
                    if (params.data.address === 'File Type') {
                      return {
                        component: 'agRichSelectCellEditor',
                        params: {
                          values: ['File Type', 'Spreadsheet', 'Document', 'PDF', 'Image'],
                        },
                      };
                    } else if (
                      ['Preparer', 'Understory Checked', 'Understory Signed Off'].includes(params.data.address) ||
                      params.data.type === 'monthlyInventory'
                    ) {
                      return { component: 'agTextCellEditor' };
                    } else if (params.data.address === 'Date Reported') {
                      return {
                        component: 'agDateCellEditor',
                        params: {
                          dateFormat: 'YYYY-MM-DD',
                        },
                      };
                    }
                    return null;
                  },

                  editable: (params: CellParams) => {
                    const colId = params.colDef.field;
                    if (!colId) return false;

                    const columnIndex = parseInt(colId.match(/(\d+)$/)[1], 10);
                    const monthDate = monthlyReportingData?.months[columnIndex];

                    if (!monthDate) return false;

                    if (isMonthLocked(monthDate)) return false;
                    return (
                      (params.data.type === 'monthlyInventory' ||
                        ['File Type', 'Date Reported', 'Preparer', 'Understory Checked', 'Understory Signed Off'].includes(params.data.address)) &&
                      editMonthlyReporting
                    );
                  },
                  cellClassRules: {
                    'ag-cell-locked-column': (params: CellParams) => {
                      if (params.data.type !== 'monthlyInventory') return false;

                      const field = params.colDef.field;
                      if (!field) return false;

                      const match = field.match(/(\d+)$/);
                      if (!match) return false;

                      const columnIndex = parseInt(match[1], 10);
                      const monthDate = monthlyReportingData?.months[columnIndex];

                      return isMonthLocked(monthDate);
                    },
                    'ag-cell-locked': (params: CellParams) => {
                      const field = params.colDef.field;
                      if (!field) return false;

                      const match = field.match(/(\d+)$/);
                      if (!match) return false;

                      const columnIndex = parseInt(match[1], 10);
                      const monthDate = monthlyReportingData?.months[columnIndex];

                      if (!isMonthLocked(monthDate)) return false;

                      return (
                        params.data.type === 'monthlyInventory' ||
                        ['File Type', 'Date Reported', 'Preparer', 'Understory Checked', 'Understory Signed Off'].includes(params.data.address)
                      );
                    },

                    'ag-cell-disable': (params: CellParams) => {
                      if (params.data.type !== 'monthlyInventory') return false;

                      if (getCellStyle(params, date)) return false;

                      const fieldName = params.colDef.field;
                      if (!fieldName) return false;

                      const columnIndexMatch = fieldName.match(/(\d+)$/);
                      if (columnIndexMatch) {
                        const columnIndex = parseInt(columnIndexMatch[1], 10);
                        const monthDate = monthlyReportingData?.months[columnIndex];

                        if (isMonthLocked(monthDate)) {
                          return false;
                        }
                      }

                      const cellMonthKey = fieldName.split('_')[1]?.slice(0, 7);

                      const addedMonthKey = params.data.added ? params.data.added.slice(0, 7) : null;

                      const deletedMonthKey = params.data.deleted ? params.data.deleted.slice(0, 7) : null;

                      if (!addedMonthKey && !deletedMonthKey) return false;

                      if (deletedMonthKey) {
                        return cellMonthKey > deletedMonthKey;
                      }

                      if (addedMonthKey) {
                        return cellMonthKey < addedMonthKey;
                      }

                      return false;
                    },

                    'error-cell': (params: CellParams) => {
                      const cellStyle = getCellStyle(params, date);
                      return cellStyle;
                    },
                    'ag-cell-editable': (params: CellParams) => {
                      const cellStyle = getCellStyle(params, date);
                      if (cellStyle !== null) {
                        return false;
                      }
                      return (
                        !getCellStyle(params, date) &&
                        ((editMonthlyReporting && params.data.type === 'monthlyInventory') ||
                          (['File Type', 'Date Reported', 'Preparer', 'Understory Checked', 'Understory Signed Off', 'Include in MCE'].includes(
                            params.data.address
                          ) &&
                            editMonthlyReporting))
                      );
                    },
                  },

                  valueSetter: (params: CellParams) => {
                    const value = params.newValue;
                    const colId = params.colDef.field;
                    const valueIndex = parseInt(colId.match(/(\d+)$/)[1], 10);
                    const updatedData: Row = { ...monthlyReportingData };
                    if (params.data.type === 'dateReported') {
                      let dateValue;
                      if (typeof params.newValue === 'string') {
                        dateValue = new Date(params.newValue);
                      } else if (params.newValue instanceof Date) {
                        dateValue = params.newValue;
                      } else {
                        return false;
                      }

                      if (!isNaN(dateValue.getTime())) {
                        const year = dateValue.getFullYear();
                        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
                        const day = String(dateValue.getDate()).padStart(2, '0');
                        updatedData[params.data.type][valueIndex] = `${year}-${month}-${day}`;
                        params.data[`monthlyInventory_${valueIndex}`] = `${year}-${month}-${day}`;
                      }
                    } else if (params.data.type !== 'monthlyInventory') {
                      updatedData[params.data.type][valueIndex] = value;
                      params.data[`monthlyInventory_${valueIndex}`] = value;
                    } else {
                      const cleanedValue = sanitizeValue(value);
                      // Each row edits its OWN location's reporting entry (matched by location id). Two locations can
                      // share a risk id (an MCE that replaces a location can reuse it), so risk id can't identify the row.
                      let location = updatedData.locations.find((loc: Row) => loc.id === params.data.id);
                      if (!location && params.data.id) {
                        location = {
                          id: params.data.id,
                          riskId: params.data.riskId,
                          values: new Array((updatedData.months || []).length).fill(null),
                        };
                        updatedData.locations.push(location);
                      }
                      if (location) {
                        location.values[valueIndex] = cleanedValue;
                        params.data[`monthlyInventory_${valueIndex}`] = cleanedValue;
                      }
                    }
                    if (updateTimeout) clearTimeout(updateTimeout);

                    updateTimeout = setTimeout(() => {
                      setMonthlyReportingData(updatedData);
                      updateTimeout = null;
                    }, 100);

                    return true;
                  },

                  cellStyle: (params: CellParams) => {
                    if (
                      params.data.type === 'label' ||
                      ['Declared Inventory', 'Bound Inventory', 'Difference', 'Rate', 'Premium Due'].includes(params.data.boundTiv) ||
                      (typeof params.data.boundTiv === 'string' && params.data.boundTiv !== 'Create Monthly Statement')
                    ) {
                      return {
                        border: '1px solid black',
                        fontWeight: 'bold',
                      };
                    } else if (params.data.address === 'Total') {
                      return {
                        fontWeight: 'bold',
                      };
                    }
                    return null;
                  },
                  cellDataType: 'text',
                },
              ],
            };
          })
        : [];

    const mceColumns: Row[] = showMCEColumns
      ? executedMCEs.map((mce, index) => {
          const eff = getMceEffectiveAtMoment(mce);
          const headerDate = eff.isValid() ? eff.format('MM/DD/YYYY') : mce?.effectiveAt || mce?.effective_at || '';
          return {
            headerName: `${headerDate} MCE`,
            children: [
              {
                headerName: 'Avg Declared Value',
                field: `mce_${mce.id}_${index}`,
                minWidth: 150,
                valueFormatter: (params: CellParams) => {
                  if (params.data.address === '% of Bound') {
                    return params.value ? `${params.value.toFixed(2)}%` : '';
                  }
                  if (params.data?.riskId || params.data?.address === 'Total') {
                    if (!params.data.formatter || typeof params.value === 'string') return;
                    return !isEmpty(params.data) && params.value !== null ? `${money(params.value)}` : '';
                  }
                  return params.value;
                },
                cellStyle: (params: CellParams) => {
                  if (params.data?.riskId) {
                    return {
                      backgroundColor: 'var(--cell-mce)',
                      fontWeight: 'bold',
                    };
                  } else if (params.data.address === 'Total') {
                    return {
                      fontWeight: 'bold',
                    };
                  }
                  return null;
                },
              },
            ],
          };
        })
      : [];

    const sortedDateColumns = sortColumnsByDate(mceColumns, monthlyColumns);
    return [...baseColumns, ...sortedDateColumns];
  };

  function sanitizeValue(value: unknown) {
    if (value === null || value === '') return null;
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return null;
    let trimmed = value.trim();
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'")))
      trimmed = trimmed.slice(1, -1).trim();
    if (!/[0-9]/.test(trimmed)) return trimmed;
    const cleaned = trimmed.replace(/\$/g, '').replace(/,/g, '').replace(/\s+/g, '');
    if (/^-?\d+(\.\d+)?$/.test(cleaned) && !/[a-zA-Z]/.test(cleaned)) return Number(cleaned);
    return trimmed;
  }

  const mapRowData = useCallback(() => {
    if (!monthlyReportingData || !monthlyReportingData.locations || !monthlyReportingData.months) {
      return [];
    }
    const months: string[] = monthlyReportingData.months;

    const basePolicyLocationByRiskId = new Map<unknown, Row>((policy?.locations || []).map((loc: Row) => [loc?.riskId, loc] as [unknown, Row]));
    const getBasePolicyBoundTiv = (riskId: unknown) => {
      if (!riskId) return 0;
      const baseLoc: Row | undefined = basePolicyLocationByRiskId.get(riskId);
      return baseLoc?.coveredValue ?? baseLoc?.covered_value ?? 0;
    };

    const locationChangeMCEs = MCEsData.filter(
      (mce) =>
        mce.type === 'locationChange' &&
        (mce.status === 'executed' || mce.status === 'released') &&
        mce.status !== 'reversal' &&
        mce.status !== 'superseded'
    ).sort((a, b) => Number(new Date(b.effectiveAt)) - Number(new Date(a.effectiveAt)));
    const mostRecentlocationChangeMCE = locationChangeMCEs[0];
    const locations: Row[] = mostRecentlocationChangeMCE?.locations?.length > 0 ? mostRecentlocationChangeMCE?.locations : policy?.locations;

    let sortedLocations = locations.sort((locA, locB) => {
      const riskIdA = locA.riskId || Number.MAX_SAFE_INTEGER;
      const riskIdB = locB.riskId || Number.MAX_SAFE_INTEGER;

      return riskIdA - riskIdB;
    });
    if (sortByGroup) {
      const allChildIds = new Set();
      for (const children of Object.values(parentToChildren)) {
        children.forEach((id) => allChildIds.add(id));
      }
      sortedLocations = [];
      locations.forEach((loc) => {
        if (allChildIds.has(loc.id)) {
          return;
        }
        sortedLocations.push(loc);
        const childrenIds = parentToChildren[loc.id] || [];
        const childrenLocs = childrenIds
          .map((childId) => locations.find((l) => l.id === childId))
          .filter((l): l is Row => Boolean(l))
          .sort((a, b) => a.riskId - b.riskId);
        sortedLocations.push(...childrenLocs);
      });
    }

    const baseRowData: Row[] = sortedLocations?.map((location) => {
      // Values belong to the location that reported them: match by location id, not risk id (Doherty 0034-01's
      // Lakeland Toyota took removed Lakeland Chrysler Collision's risk 1; matching by risk showed Toyota's
      // inventory on both rows).
      const matchingLocation = monthlyReportingData.locations.find((reported: Row) => reported.id === location.id);
      const monthlyValues = matchingLocation
        ? months.reduce<Row>((acc, date, index) => {
            acc[`monthlyInventory_${date}_${index}`] =
              matchingLocation.values?.[index] === null || isNaN(parseInt(matchingLocation.values?.[index]))
                ? matchingLocation.values?.[index]
                : parseFloat(matchingLocation.values?.[index]);
            return acc;
          }, {})
        : {};

      const mceValues = executedMCEs.reduce<Row>((acc, mce, mceIndex) => {
        const mceLocation = mce.locations?.find((loc: Row) => loc.id === location.id);
        const mceCalculations = mceLocation && mce?.calculations?.find((cal: Row) => cal.locationId === mceLocation.id);
        acc[`mce_${mce.id}_${mceIndex}`] = mceCalculations?.declaredInventory || 0;
        return acc;
      }, {});

      return {
        name: location.name,
        address: location.address,
        id: location.id,
        formatter: true,
        type: 'monthlyInventory',
        riskId: location.riskId,
        boundTiv: getBasePolicyBoundTiv(location.riskId),
        deleted: location?.deleted || '',
        added: location?.added || '',
        ...monthlyValues,
        ...mceValues,
      };
    });

    const totalRow: Row = {
      address: 'Total',
      formatter: true,
      boundTiv: sumValues(baseRowData?.map((row) => row.boundTiv)),
      ...months.reduce<Row>((acc, date, index) => {
        acc[`monthlyInventory_${date}_${index}`] = sumValues(baseRowData.map((row) => row[`monthlyInventory_${date}_${index}`] || 0));
        return acc;
      }, {}),
      ...executedMCEs.reduce<Row>((acc, mce, index) => {
        acc[`mce_${mce.id}_${index}`] = sumValues(baseRowData.map((row) => row[`mce_${mce.id}_${index}`] || 0));
        return acc;
      }, {}),
    };

    const getMostRecentMceTotalForMonthEnd = (monthEnd: string) => {
      if (!showMCEColumns) return totalRow.boundTiv;
      if (!monthEnd) return totalRow.boundTiv;

      const monthEndMoment = moment.utc(monthEnd, 'YYYY-MM-DD', true);
      if (!monthEndMoment.isValid()) return totalRow.boundTiv;

      for (let i = executedMCEs.length - 1; i >= 0; i--) {
        const mce = executedMCEs[i] as Row;
        const eff = getMceEffectiveAtMoment(mce);
        if (!eff.isValid()) continue;
        if (eff.isSameOrBefore(monthEndMoment, 'day')) {
          const key = `mce_${mce.id}_${i}`;
          const divisor = totalRow[key] ?? totalRow.boundTiv;
          return divisor;
        }
      }

      return totalRow.boundTiv;
    };

    const percentageRow = {
      address: '% of Bound',
      formatter: true,
      boundTiv: totalRow.boundTiv ? (totalRow.boundTiv / totalRow.boundTiv) * 100 : 0,
      ...months.reduce<Row>((acc, date, index) => {
        const totalValue = totalRow[`monthlyInventory_${date}_${index}`] || 0;
        const divisor = getMostRecentMceTotalForMonthEnd(date);
        acc[`monthlyInventory_${date}_${index}`] = divisor ? (totalValue / divisor) * 100 : 0;
        return acc;
      }, {}),
      ...executedMCEs.reduce<Row>((acc, mce, index) => {
        // For any MCE (yellow) column, % of Bound is always 100%.
        acc[`mce_${mce.id}_${index}`] = 100;
        return acc;
      }, {}),
    };

    const checkBoxRow = {
      address: 'Include in MCE',
      formatter: false,
      ...months.reduce<Row>((acc, date, index) => {
        acc[`monthlyInventory_${date}_${index}`] = false;
        return acc;
      }, {}),
    };

    const reportedRow = (address: string, type: string) => ({
      address,
      boundTiv: null,
      formatter: false,
      type,
      ...months.reduce<Row>((acc, date, index) => {
        acc[`monthlyInventory_${date}_${index}`] = monthlyReportingData[type]?.[index] || null; // Default to null if undefined
        return acc;
      }, {}),
    });

    const additionalRows = [
      reportedRow('Date Reported', 'dateReported'),
      reportedRow('Preparer', 'preparer'),
      reportedRow('File Type', 'fileType'),
      reportedRow('Understory Checked', 'uwChecked'),
      reportedRow('Understory Signed Off', 'uwSignedOff'),
      {
        address: 'Create MCE',
        name: 'Combine Locations',
        boundTiv: 'Create Monthly Statement',
      },
      {},
    ];

    return [...baseRowData, {}, totalRow, percentageRow, checkBoxRow, {}, ...additionalRows];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyReportingData, policy, sortByGroup, showMCEColumns, executedMCEs]);

  const getRowHeight = (params: CellParams) => {
    if (params.data.address === 'Create MCE' || params.data.name === 'Combine Locations' || params.data.boundTiv === 'Create Monthly Statement') {
      return 80;
    }
    return undefined;
  };
  const handleClipboardData = useCallback((params: CellParams) => {
    const { data } = params;
    if (data.length > 0 && data[data.length - 1].every((cell: unknown) => cell === '')) {
      return data.slice(0, data.length - 1);
    }
    return data;
  }, []);

  useEffect(() => {
    if (policy.locations.length) setRowData(mapRowData());
  }, [policy, mapRowData]);

  useEffect(() => {
    if (policy.locations.length && executedStatements.length) {
      setRowData(mapRowData());
    }
  }, [policy, mapRowData, executedStatements]);

  const handleClose = () => {
    setShowCombineLocationsModal(false);
  };
  return (
    <div className="grid-container min-h-[500px]">
      <style>
        {`
    .ag-cell-locked-column {
      background-color: var(--cell-locked) !important;
    }
  `}
      </style>
      <div className="mb-4">
        <label>
          <input type="radio" checked={sortByGroup} onChange={() => setSortByGroup(true)} name={sortName} className="mt-2 ml-2" />
          <span className="ml-2">Sort by Location Group</span>
        </label>
        <label className="ml-6">
          <input type="radio" checked={!sortByGroup} onChange={() => setSortByGroup(false)} name={sortName} className="mt-2 ml-2" />
          <span className="ml-2">Default Sort</span>
        </label>
      </div>
      <Grid
        data={rowData}
        columns={monthlyReportingData && createColumns()}
        floatingFilter={false}
        setGridReference={(ref: unknown) => {
          gridRef.current = ref;
          setGridReference(ref);
        }}
        shouldShowPagination={false}
        getRowHeight={getRowHeight}
        processDataFromClipboard={handleClipboardData}
        showStatementsListModal={showStatementsListModal}
        setShowStatementsListModal={setShowStatementsListModal}
        onCellDoubleClicked={(params: CellParams) => {
          const colId = params.colDef.field;
          if (!colId) return;

          const match = colId.match(/(\d+)$/);
          if (!match) return;

          const columnIndex = parseInt(match[1], 10);
          const monthDate = monthlyReportingData?.months[columnIndex];
          if (!monthDate) return;

          if (isMonthLocked(monthDate)) {
            notifyError('Executed Monthly Statement exists for this month.');
          }
        }}
      />
      {showModal && (
        <CreateRetroMCEModal showModal={showModal} setShowModal={setShowModal} policyId={policy.id} getPayload={getPayload} createMCE={createMCE} />
      )}
      {showCombineLocationsModal && <CombineLocationModal showCombineLocationsModal={showCombineLocationsModal} handleClose={handleClose} />}
      {showMonthlyStatementModal && (
        <CreateMonthlyStatementModal
          showModal={showMonthlyStatementModal}
          setShowModal={setShowMonthlyStatementModal}
          policyId={policy.id}
          insertMonthlyStatement={insertMonthlyStatement}
          refreshStatements={fetchAllStatements}
          getQuarterlyDates={getQuarterlyDates}
          getBillingConfig={getBillingConfig}
          putBillingConfig={putBillingConfig}
          getLatestStatementCommissionRate={getLatestStatementCommissionRate}
        />
      )}

      {showStatementsListModal && (
        <MonthlyStatementsModal
          open={showStatementsListModal}
          onClose={() => setShowStatementsListModal(false)}
          policyId={policy.id}
          statements={allStatements}
          onExecuteStatement={handleExecuteStatement}
          refreshStatements={fetchAllStatements}
          deleteMonthlyStatement={deleteMonthlyStatement}
          getStatementJsonBlob={getStatementJsonBlob}
        />
      )}
    </div>
  );
});
MonthlyReporting.displayName = 'MonthlyReporting';
